import './styles.sass';

import React, { useEffect, useId, useImperativeHandle, useRef, useState } from 'react';
import PropTypes from 'prop-types';

import { map } from 'lodash-es';

import fetch from 'util/fetch';
import NumberHelper from 'util/number_helper';
import * as QueryFetcher from 'components/Explorer/models/query_fetcher';
import FilterSet from 'components/Explorer/models/filter_set';
import HelpLink from '../../HelpLink';
import useQueryParams from 'hooks/useQueryParams';

const ANALYTICS_DIALOGUE_NAME = 'Scholarly Identifiers';

const countTokens = (text) => text.split(/\s+/).filter((t) => t.length).length;

const trackDialogueEvent = (eventName, properties = {}) => {
  Analytics.trackEvent(`Advanced Search: Modal Dialogue ${eventName}`, {
    dialogue_name: ANALYTICS_DIALOGUE_NAME,
    ...properties
  });
};

const IdentifierFilter = ({ filterSet, trackSave = () => {}, reportUnsavableDraft = () => {}, draftRef }) => {
  const params = useQueryParams();
  const textareaId = useId();
  const titleId = useId();
  const counterId = useId();
  const errorId = useId();

  const hasList = Boolean(filterSet.get('identifier_list_id') || filterSet.get('identifier'));

  const [identifierList, setIdentifierList] = useState(null);
  const [loading, setLoading] = useState(hasList);
  const [editing, setEditing] = useState(false);
  const [content, setContent] = useState('');
  const [saving, setSaving] = useState(false);
  const [saveFailed, setSaveFailed] = useState(false);
  // Saving, clearing and cancelling swap the field's contents with no
  // change of focus to tell screen reader users what happened. A live
  // region only reads out a change, so the message is dropped once the user
  // starts on the field again, letting a repeat of it be heard.
  const [statusMessage, setStatusMessage] = useState('');
  // Blur can fire again before the in-flight save re-renders, so the guard
  // can't wait for state.
  const savingRef = useRef(false);
  // The field can be unmounted with a save still in flight, which discards
  // the draft, so a failure then has nothing left to report against.
  const mountedRef = useRef(false);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const textareaRef = useRef(null);
  const editButtonRef = useRef(null);
  // Edit, Cancel and Clear swap which controls are rendered, so the element
  // that should take focus only exists after the next render.
  const focusAfterRenderRef = useRef(null);
  // The modal's Opened and Filled events fired once per visit to the
  // editor. A session here runs from opening the field until it is saved,
  // cleared, cancelled or left unchanged.
  const sessionRef = useRef({ opened: false, filled: false });
  // Focus the field moves itself (after Clear) isn't the user opening it.
  const movingFocusRef = useRef(false);

  const endSession = () => {
    sessionRef.current = { opened: false, filled: false };
  };

  const trackOpened = (action) => {
    if (sessionRef.current.opened) return;
    sessionRef.current.opened = true;
    trackDialogueEvent('Opened', { action });
  };

  useEffect(() => {
    const target = focusAfterRenderRef.current;
    if (!target) return;

    focusAfterRenderRef.current = null;
    movingFocusRef.current = true;
    target.current?.focus();
    movingFocusRef.current = false;
  });

  // Hydrate from the URL once on mount. Subsequent save/clear update the
  // local state directly — re-firing on `hasList` would race against the
  // save's setIdentifierList and overwrite it with a stale hydration response.
  useEffect(() => {
    if (!hasList) return undefined;

    setLoading(true);
    const request = QueryFetcher.fetchJSON('filters/identifier_list', params);

    request.then((err, response) => {
      if (err?.name === 'AbortError') return;
      if (!err && response.body) {
        setIdentifierList(response.body);
      }
      setLoading(false);
    });

    return () => request.abort();
  }, []);

  const maxTokens = window.current_user.maxIdentifierTokens;
  const tokenCount = countTokens(content);
  const tooManyIdentifiers = tokenCount > maxTokens;
  const showingEditor = !loading && (!identifierList || editing);
  // A draft that can't be saved as it stands would be left out of a search.
  const unsavableDraft = tooManyIdentifiers || saveFailed;

  useEffect(() => {
    reportUnsavableDraft(unsavableDraft);
  }, [unsavableDraft]);
  useEffect(() => () => reportUnsavableDraft(false), []);

  // Back to rest: the summary if a list is saved, an empty textarea if not.
  const closeEditor = () => {
    setEditing(false);
    setContent('');
    setSaveFailed(false);
    endSession();
  };

  const save = () => {
    // A failed save would fail again unchanged; typing clears saveFailed.
    if (savingRef.current || tokenCount === 0 || unsavableDraft) return;
    savingRef.current = true;
    setSaving(true);
    // Otherwise a failed save would leave the previous announcement to be
    // read out again once "Saving…" goes.
    setStatusMessage('');
    trackSave(persist());
  };

  const persist = async () => {
    try {
      const response = await fetch('/explorer/identifier_lists', {
        method: 'post',
        body: JSON.stringify({ identifier_list: { identifiers: content } })
      });
      const nextIdentifierList = await response.json();

      trackDialogueEvent('Data Updated', { action: identifierList ? 'Edit' : 'Create', items_count: tokenCount });
      filterSet.set('identifier_list_id', nextIdentifierList.id);
      filterSet.clear('identifier');
      setIdentifierList(nextIdentifierList);
      // Focus can return to the read-only textarea mid-save; it shouldn't be
      // lost when the summary replaces it.
      if (document.activeElement === textareaRef.current) focusAfterRenderRef.current = editButtonRef;
      closeEditor();
      setStatusMessage(
        I18n.t('Explorer.AdvancedSearch.fields.identifiers.saved_announcement', {
          count: tokenCount,
          formattedCount: NumberHelper.formatNumberWithDelimiter(tokenCount)
        })
      );
      return true;
    } catch (_error) {
      if (!mountedRef.current) return false;
      setSaveFailed(true);
      window.alert(I18n.t('Explorer.AdvancedSearch.fields.identifiers.editor.error'));
      return false;
    } finally {
      savingRef.current = false;
      setSaving(false);
    }
  };

  const clear = () => {
    filterSet.clear('identifier_list_id');
    filterSet.clear('identifier');
    setIdentifierList(null);
    setStatusMessage(I18n.t('Explorer.AdvancedSearch.fields.identifiers.cleared_announcement'));

    trackDialogueEvent('Cleared');
    closeEditor();
  };

  const leaveField = () => {
    if (editing && content === identifierList.content) closeEditor();
    else if (tokenCount > 0) save();
    else if (editing) clear();
    else endSession();
  };

  // Run search can be pressed without focus leaving the field (from a
  // screen reader's browse mode, say), so submitting leaves it too.
  useImperativeHandle(draftRef, () => ({ finish: leaveField }));

  const onEditorBlur = (e) => {
    if (e.currentTarget.contains(e.relatedTarget)) return;
    // Switching window or tab blurs the textarea too, but it stays the active
    // element: the user hasn't left the field and will expect to carry on
    // typing when they return.
    if (document.activeElement === e.target) return;

    leaveField();
  };

  const onTextareaFocus = () => {
    if (content === '' && !movingFocusRef.current) trackOpened('Create');
  };

  const onTextareaChange = (e) => {
    const value = e.target.value;
    if (!sessionRef.current.filled && value.length > content.length) {
      sessionRef.current.filled = true;
      trackDialogueEvent('Filled');
    }
    setContent(value);
    setSaveFailed(false);
    setStatusMessage('');
  };

  const onClickEdit = () => {
    trackOpened('Edit');
    setStatusMessage('');
    setContent(identifierList.content);
    setEditing(true);
    focusAfterRenderRef.current = textareaRef;
  };

  const onClickClear = () => {
    clear();
    focusAfterRenderRef.current = textareaRef;
  };

  // Cancelling an edit reverts to the saved list without touching the
  // filter; with nothing saved there is nothing to revert to, so it just
  // empties the draft.
  const onClickCancel = () => {
    trackDialogueEvent('Closed');
    closeEditor();
    if (editing) {
      setStatusMessage(I18n.t('Explorer.AdvancedSearch.fields.identifiers.cancelled_announcement'));
      focusAfterRenderRef.current = editButtonRef;
    } else {
      setStatusMessage(I18n.t('Explorer.AdvancedSearch.fields.identifiers.cleared_announcement'));
      focusAfterRenderRef.current = textareaRef;
    }
  };

  const descriptionString = () => {
    const descriptions = map(identifierList.counts, (count, type) => {
      return (
        count > 0 &&
        I18n.t(type, {
          scope: 'Explorer.AdvancedSearch.fields.identifiers.count',
          formattedCount: NumberHelper.formatNumberWithDelimiter(count),
          count
        })
      );
    }).filter((d) => d);
    if (descriptions.length > 0) return descriptions.join(', ');
    return I18n.t('Explorer.AdvancedSearch.fields.identifiers.empty');
  };

  const savedContent = () => (
    <div className="IdentifierFilter-content">
      <div className="IdentifierFilter-identifier-description">{descriptionString()}</div>
      <div className="IdentifierFilter-identifier-edit-buttons">
        <button type="button" className="IdentifierFilter-edit-button" onClick={onClickEdit} ref={editButtonRef}>
          {I18n.t('Explorer.AdvancedSearch.fields.identifiers.edit')}
        </button>
        <button type="button" className="IdentifierFilter-clear-button" onClick={onClickClear}>
          {I18n.t('Explorer.AdvancedSearch.fields.identifiers.clear')}
        </button>
      </div>
    </div>
  );

  const editorContent = () => (
    <div className="IdentifierFilter-inline" onBlur={onEditorBlur}>
      <textarea
        id={textareaId}
        className="IdentifierFilter-inline-textarea"
        value={content}
        // Anything typed now would be wiped when the save completes.
        readOnly={saving}
        placeholder={I18n.t('Explorer.AdvancedSearch.fields.identifiers.editor.placeholder')}
        aria-labelledby={titleId}
        aria-describedby={tooManyIdentifiers ? `${counterId} ${errorId}` : counterId}
        aria-invalid={tooManyIdentifiers || undefined}
        onFocus={onTextareaFocus}
        onChange={onTextareaChange}
        ref={textareaRef}
      />
      <div id={counterId} className="IdentifierFilter-inline-meta">
        {I18n.htmlSafe('Explorer.AdvancedSearch.fields.identifiers.editor.meta', {
          maxCount: NumberHelper.formatNumberWithDelimiter(maxTokens),
          formattedCount: NumberHelper.formatNumberWithDelimiter(tokenCount)
        })}
      </div>
      {tooManyIdentifiers && (
        <div id={errorId} className="IdentifierFilter-inline-error" role="alert">
          {I18n.t('Explorer.AdvancedSearch.fields.identifiers.too_many_alert')}
        </div>
      )}
      {(editing || content.length > 0) && (
        <div className="IdentifierFilter-inline-buttons">
          {/* Keeping focus in the textarea on mousedown stops browsers that
              don't focus buttons on click from reporting the press as focus
              leaving the field, which would save the draft. */}
          <button
            type="button"
            className="IdentifierFilter-inline-cancel"
            onMouseDown={(e) => e.preventDefault()}
            onClick={onClickCancel}
            // A cancel mid-save would be overtaken by the save completing.
            disabled={saving}
          >
            {I18n.t('Explorer.AdvancedSearch.fields.identifiers.editor.cancel')}
          </button>
        </div>
      )}
      {saving && <div className="IdentifierFilter-inline-saving" />}
    </div>
  );

  return (
    <div className="Explorer-AdvancedSearch-field-container IdentifierFilter">
      {/* aria-labelledby names the textarea from the title alone; the label
          as a whole would also pull in the help link's long aria-label. */}
      <label htmlFor={showingEditor ? textareaId : undefined}>
        <span id={titleId}>{I18n.t('Explorer.AdvancedSearch.fields.identifiers.title')}</span>
        <HelpLink
          href={I18n.t('Explorer.AdvancedSearch.help_uri')}
          tip={I18n.t('Explorer.AdvancedSearch.fields.identifiers.description')}
          analyticsFieldName="Scholarly Identifiers"
        />
      </label>
      <div className="visually-hidden" role="status">
        {saving ? I18n.t('Explorer.AdvancedSearch.fields.identifiers.saving') : statusMessage}
      </div>
      <fieldset aria-busy={saving}>
        <legend>{I18n.t('Explorer.AdvancedSearch.fields.identifiers.title')}</legend>
        {loading ? (
          <div className="IdentifierFilter-loading-message">
            {I18n.t('Explorer.AdvancedSearch.fields.identifiers.loading')}
          </div>
        ) : showingEditor ? (
          editorContent()
        ) : (
          savedContent()
        )}
      </fieldset>
    </div>
  );
};

IdentifierFilter.propTypes = {
  filterSet: PropTypes.instanceOf(FilterSet).isRequired,
  // Called with each save's promise, which resolves to whether the filter
  // set now holds the new list.
  trackSave: PropTypes.func,
  // Called with whether the draft can't be saved as it stands (over the
  // limit, or its save failed), so the search can wait for it to be fixed.
  reportUnsavableDraft: PropTypes.func,
  // Given `finish()`, which handles the draft as leaving the field would.
  draftRef: PropTypes.object
};

export default IdentifierFilter;
